/**
 * MetricsSection - Impactful capability metrics band
 * Shows key numbers about NARA's capabilities
 */
import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Container } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CategoryIcon from '@mui/icons-material/Category';
import MemoryIcon from '@mui/icons-material/Memory';

import { COLORS, TRANSITIONS, SPACING } from './landingTheme';
import { useScrollReveal } from './animations';

// Animated counter hook
function useCounter(end, duration = 1800, isVisible = false) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isVisible || started.current) return;
    started.current = true;

    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isVisible, end, duration]);

  return count;
}

const METRICS = [
  {
    value: 6,
    suffix: '',
    label: 'Herramientas IA',
    description: 'Diagnóstico, citas, memoria, imágenes, documentos y escalamiento',
    icon: AutoAwesomeIcon,
    color: '#f59e0b',
  },
  {
    value: 365,
    suffix: '',
    label: 'Días de memoria',
    description: 'Recuerda historial, alergias y preferencias de cada paciente',
    icon: MemoryIcon,
    color: '#8b5cf6',
  },
  {
    value: 9,
    suffix: '',
    label: 'Tipos de memoria',
    description: 'Alergias, medicamentos, síntomas, diagnósticos, preferencias y más',
    icon: CategoryIcon,
    color: '#0ea5e9',
  },
  {
    value: 4,
    suffix: '',
    label: 'Modelos de IA',
    description: 'GPT-4o, GPT-4o-mini, Claude 3.5 Sonnet y Google Embeddings',
    icon: PsychologyIcon,
    color: '#10b981',
  },
];

const MetricCard = ({ metric, isVisible, delay }) => {
  const IconComponent = metric.icon;
  const count = useCounter(metric.value, 1800 + delay * 200, isVisible);

  return (
    <Box
      sx={{
        textAlign: 'center',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 0 },
        position: 'relative',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay * 0.12}s`,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          background: `${metric.color}10`,
          border: `1px solid ${metric.color}20`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <IconComponent sx={{ fontSize: 22, color: metric.color }} />
      </Box>

      {/* Number */}
      <Typography
        sx={{
          fontSize: { xs: '2.25rem', md: '2.75rem' },
          fontWeight: 800,
          color: COLORS.textPrimary,
          lineHeight: 1,
          letterSpacing: '-0.03em',
          mb: 0.5,
        }}
      >
        {metric.prefix || ''}{count}{metric.suffix || ''}
      </Typography>

      {/* Label */}
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          color: COLORS.textPrimary,
          mb: 0.5,
        }}
      >
        {metric.label}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: COLORS.textMuted,
          lineHeight: 1.5,
          maxWidth: 200,
          mx: 'auto',
        }}
      >
        {metric.description}
      </Typography>
    </Box>
  );
};

export default function MetricsSection() {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 5, md: 7 },
        background: COLORS.white,
        position: 'relative',
        overflow: 'hidden',
        borderBottom: `1px solid ${COLORS.gray200}`,
      }}
    >
      {/* Subtle accent line at top */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: 1,
          background: `linear-gradient(90deg, transparent, ${COLORS.accent}30, transparent)`,
        }}
      />

      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 1, md: 0 },
            // Vertical dividers on desktop
            '& > *:not(:last-child)': {
              md: {
                borderRight: `1px solid ${COLORS.gray200}`,
              },
            },
          }}
        >
          {METRICS.map((metric, idx) => (
            <MetricCard
              key={idx}
              metric={metric}
              isVisible={isVisible}
              delay={idx}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
}
